import { NavLink, Outlet } from 'react-router-dom';
import {
  CalendarDays,
  Clock,
  FileText,
  FolderClosed,
  Home,
  Inbox,
  LogOut,
  PieChart,
  Receipt,
  Settings,
  SlidersHorizontal,
  Users,
} from 'lucide-react';

import { useQuery } from '@tanstack/react-query';

import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/use-auth';
import { ticketsVisibility } from '@/lib/tickets-api';
import { avatarUrl } from '@/lib/avatar-url';
import { Avatar } from '@/components/ui/avatar';
import { NotificationsBell } from '@/components/NotificationsBell';
import { PrivacyPolicyGate } from '@/components/PrivacyPolicyGate';
import { WhatsNewPanel } from '@/components/WhatsNewPanel';

export function AppLayout() {
  const { user, logout } = useAuth();

  const canSeeTeam =
    user?.role === 'MANAGER' || user?.role === 'HR_ADMIN' || user?.role === 'SUPER_ADMIN';
  const canSeeAdmin = user?.role === 'HR_ADMIN' || user?.role === 'SUPER_ADMIN';
  // "Tickets viaje" solo aparece si el empleado tiene una carpeta abierta
  // de un viaje activo (RRHH lo asignó). Si no, desaparece del menú.
  const ticketsVisible = useQuery({
    queryKey: ['tickets', 'visibility'],
    queryFn: ticketsVisibility,
  });
  const hasOpenTrips = ticketsVisible.data?.hasOpenFolders ?? false;
  // Feature flag del backend (/auth/me). Si el bloque Documentos está
  // apagado, ocultamos sus entradas de menú. Ausencia = habilitado.
  const documentsEnabled = user?.features?.documents ?? true;

  return (
    <div className="min-h-dvh flex flex-col md:flex-row">
      <aside className="md:w-60 md:flex md:flex-col border-b md:border-b-0 md:border-r border-border bg-paper">
        {/* Brand row */}
        <div className="px-4 py-4 md:py-5 border-b border-border flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="text-base font-bold tracking-[0.14em] text-foreground leading-none">
              ICARIA
            </span>
            <span className="font-mono text-[9px] font-medium uppercase tracking-[0.18em] text-muted-foreground leading-none">
              ATELIER
            </span>
          </div>
          <NotificationsBell />
        </div>

        {/* Section label + nav */}
        <div className="px-3 pt-3.5 pb-1.5 hidden md:block">
          <p className="font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Mi espacio
          </p>
        </div>
        <nav className="px-2 flex md:flex-col gap-1 overflow-x-auto md:overflow-visible flex-1">
          <NavItem to="/" end icon={<Home className="h-3.5 w-3.5" />} label="Inicio" />
          <NavItem to="/jornada" end icon={<Clock className="h-3.5 w-3.5" />} label="Mi jornada" />
          <NavItem
            to="/solicitudes"
            end
            icon={<Inbox className="h-3.5 w-3.5" />}
            label="Solicitudes"
          />
          {documentsEnabled ? (
            <NavItem
              to="/documentos"
              end
              icon={<FolderClosed className="h-3.5 w-3.5" />}
              label="Documentos"
            />
          ) : null}
          {hasOpenTrips ? (
            <NavItem
              to="/tickets"
              icon={<Receipt className="h-3.5 w-3.5" />}
              label="Tickets viaje"
            />
          ) : null}
          <NavItem
            to="/estadisticas"
            end
            icon={<PieChart className="h-3.5 w-3.5" />}
            label="Estadísticas"
          />
          <NavItem
            to="/equipo/calendario"
            end
            icon={<CalendarDays className="h-3.5 w-3.5" />}
            label="Calendario equipo"
          />
          {canSeeTeam ? (
            <NavItem to="/equipo" end icon={<Users className="h-3.5 w-3.5" />} label="Equipo" />
          ) : null}
          {canSeeTeam ? (
            <NavItem
              to="/reportes"
              end
              icon={<FileText className="h-3.5 w-3.5" />}
              label="Reportes"
            />
          ) : null}
          {canSeeTeam && documentsEnabled ? (
            <NavItem
              to="/admin/documentos"
              end
              icon={<FolderClosed className="h-3.5 w-3.5" />}
              label="Gestión docs"
            />
          ) : null}
          {canSeeAdmin ? (
            <NavItem
              to="/admin/viajes"
              icon={<Receipt className="h-3.5 w-3.5" />}
              label="Tickets equipo"
            />
          ) : null}
          {canSeeAdmin ? (
            <NavItem
              to="/admin"
              icon={<Settings className="h-3.5 w-3.5" />}
              label="Administración"
            />
          ) : null}
          <NavItem
            to="/ajustes"
            end
            icon={<SlidersHorizontal className="h-3.5 w-3.5" />}
            label="Ajustes"
          />
        </nav>

        {/* Me + signout */}
        {user ? (
          <div className="hidden md:block px-3 pt-3 pb-3 border-t border-border">
            <div className="flex items-center gap-2.5 px-2">
              <Avatar
                fullName={user.fullName}
                color={user.profileColor}
                avatarUrl={avatarUrl(user.id, user.avatarUpdatedAt)}
              />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm truncate leading-tight">{user.fullName}</p>
                <p className="text-[11px] text-muted-foreground truncate leading-tight">
                  {roleLabel(user.role)}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => void logout()}
              className="mt-2 w-full flex items-center gap-2.5 px-2 py-2 rounded-xs text-[12.5px] font-medium text-ink-2 border border-transparent hover:bg-card hover:text-destructive hover:border-border transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span>Cerrar sesión</span>
            </button>
          </div>
        ) : null}
      </aside>

      <main className="flex-1 min-w-0 bg-background">
        <Outlet />
      </main>
      <PrivacyPolicyGate />
      <WhatsNewPanel />
    </div>
  );
}

function NavItem({
  to,
  icon,
  label,
  end,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 px-3 py-2 rounded-xs text-[13px] font-medium whitespace-nowrap border-l-2 transition-colors',
          isActive
            ? 'bg-card text-foreground border-icaria-600 shadow-sm [&>svg]:text-icaria-600 [&>svg]:opacity-100'
            : 'bg-transparent text-ink-2 border-transparent hover:bg-black/[0.025] [&>svg]:opacity-60',
        )
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}

function roleLabel(role: string): string {
  switch (role) {
    case 'EMPLOYEE':
      return 'Empleado';
    case 'MANAGER':
      return 'Manager';
    case 'HR_ADMIN':
      return 'RRHH';
    case 'SUPER_ADMIN':
      return 'Admin';
    default:
      return role;
  }
}
